import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Copy, Link2, Loader2, Share2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShareFilmCard({ token, lovedOne, sharePath, onChange }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = sharePath ? window.location.origin + sharePath : "";

  const copy = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success("Link copied. Paste it into a text, email or message.");
    } catch {
      toast.message("Copy this link:", { description: link });
    }
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/film/${token}/share`);
      onChange(data.share_path);
      const link = window.location.origin + data.share_path;
      if (navigator.share && window.matchMedia("(pointer: coarse)").matches) {
        await navigator.share({ title: `${lovedOne}'s tribute film`, url: link }).catch(() => copy(link));
      } else {
        await copy(link);
      }
    } catch {
      toast.error("Could not create the link. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    if (!window.confirm("Turn off this link? Anyone who has it will no longer be able to watch. You can make a new one any time.")) return;
    await axios.delete(`${API}/film/${token}/share`).then(() => onChange(null)).catch(() => toast.error("Could not turn off the link."));
  };

  return (
    <section data-testid="share-film-card" className="card-surface p-6 sm:p-7 min-w-0">
      <div className="flex items-center gap-2 text-[11px] tracking-widest uppercase text-[#C5A55A]/80"><Share2 size={13} /> Share with relatives</div>
      <p className="mt-3 text-sm text-[#A0988E] spectral leading-relaxed">
        A watch-only link for family and friends. It opens just the film — not your photos or this page.
      </p>
      {!sharePath ? (
        <button data-testid="share-create-button" onClick={create} disabled={busy} className="btn-primary mt-5 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />} Get a share link
        </button>
      ) : (
        <>
          <div className="mt-5 flex items-center gap-2 rounded-full border border-[rgba(197,165,90,0.3)] pl-4 pr-1.5 py-1.5">
            <span data-testid="share-link-text" className="flex-1 min-w-0 truncate text-sm text-[#F5F0E8]">{url}</span>
            <button data-testid="share-copy-button" onClick={() => copy(url)} className="btn-primary text-xs py-2 px-3 whitespace-nowrap">
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <button data-testid="share-stop-button" onClick={stop} className="mt-3 text-xs text-[#A0988E] hover:text-[#E6B89C]">Turn off this link</button>
        </>
      )}
    </section>
  );
}
